import {
  type ChangeEvent,
  type Dispatch,
  type FC,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import Switch from '__components/Kizen/Switch';
import { SMART_CONNECTOR_TYPES } from 'pages/SmartConnectors/constants';
import type {
  FlowLoadErrors,
  GenericOption,
  RunSmartConnectorFlowLoad,
} from 'pages/SmartConnectors/types';
import { FlowBuilderContext } from 'pages/SmartConnectors/SmartConnector/SmartConnectorBuilder/FlowBuilder/context';
import { Section } from './Section';
import { useFlashTransition } from 'hooks/useFlashState';

export const SmartConnectorSettings: FC<{
  smartConnectorToRunId?: string | null;
  smartConnectorToRunName?: string;
  waitForExecution: boolean;
  onChange: Dispatch<SetStateAction<RunSmartConnectorFlowLoad>>;
  error?: string[];
  resetError: (
    patch:
      | Partial<FlowLoadErrors>
      | ((prev: FlowLoadErrors) => Partial<FlowLoadErrors>)
  ) => void;
}> = ({
  smartConnectorToRunId,
  smartConnectorToRunName,
  waitForExecution,
  onChange,
  error,
  resetError,
}) => {
  const { t } = useTranslation();
  const { smartConnector, smartConnectors } = useContext(FlowBuilderContext);

  const childOptions = useMemo(
    () =>
      smartConnectors
        .filter(
          (connector) =>
            connector.connector_type === SMART_CONNECTOR_TYPES.bulkaction &&
            connector.id !== smartConnector.id
        )
        .map((connector) => ({
          value: connector.id,
          label: connector.name,
        })),
    [smartConnectors, smartConnector.id]
  );

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) {
      flashErrorMessage(error);
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    return () => cancelFlash();
  }, [error, flashErrorMessage, cancelFlash]);

  const validate = useMemo(
    () => ({
      message,
      showMessage,
    }),
    [message, showMessage]
  );

  const handleChangeSmartConnector = ({ value }: GenericOption) => {
    if (value !== smartConnectorToRunId) {
      onChange((prev) => ({
        ...prev,
        smart_connector_to_run: value,
        return_scopes: [],
        input_data: {
          ...prev.input_data,
          variable: undefined,
          filter: undefined,
        },
      }));
      resetError((prev) => ({
        ...prev,
        smart_connector_to_run: undefined,
        error_handling: {},
        input_data: {
          ...prev.input_data,
          variable: undefined,
          filter: undefined,
        },
        return_scopes: undefined,
      }));
    }
  };

  const handleChangeWaitForExecution = (e: ChangeEvent<HTMLInputElement>) => {
    onChange((prev) => ({
      ...prev,
      wait_for_execution: e.target.checked,
      return_scopes: [],
      error_handling: {
        ...prev.error_handling,
        on_error: 'ignore',
        on_child_cancelled: 'continue',
      },
    }));
    resetError((prev) => ({
      ...prev,
      wait_for_execution: undefined,
      return_scopes: undefined,
      error_handling: {
        ...prev.error_handling,
        on_error: undefined,
        on_child_cancelled: undefined,
      },
    }));
  };

  return (
    <Section title={t('SmartConnector Settings')}>
      <div ref={wrapperRef}>
        <Select
          label={t('Choose SmartConnector')}
          placeholder={t('Choose SmartConnector')}
          options={childOptions}
          value={smartConnectorToRunId}
          onChange={handleChangeSmartConnector}
          margin
          menuInline={!smartConnectorToRunId}
          error={!!error}
          validate={validate}
        />
      </div>
      {smartConnectorToRunName ? (
        <Switch
          label={t('Wait for "{{name}}" to Complete', {
            name: smartConnectorToRunName,
          })}
          textPlacement="right"
          checked={waitForExecution}
          onChange={handleChangeWaitForExecution}
          removeMargin
        />
      ) : null}
    </Section>
  );
};
